import * as React from "react";
import { Button } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTableKindId } from "../../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../../newcomponents/table/data/useTableItemQueryData";
import { Workload } from "../../../../schema/types/workload/entity";
import { Agent } from "../../../../schema/types/agent/entity";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { Table } from "../../../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { TableModal } from "../../../../components/antd/TableModal";
import { TableHeaderRefreshButton } from "../../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../../newcomponents/table/components/TableCplnQuery";
import { GvcColumn } from "../../../../newcomponents/table/columns/wellKnown/gvcColumn";
import { getWorkloadBasicTableItem, WorkloadBasicTableItem } from "../../../../mst/kinds/workload.helpers";
import { AgentTableItem, getAgentTableItem } from "../../../../mst/kinds/agent.helpers";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { cloneDeep, get } from "lodash";
import { store } from "../../../../store";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { KindSelectState } from "../../../../components/antd/kindselect/KindSelect";

interface Props {}
export const InternalWorkloadItems: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const workloadLink = useCpFormValue(`meta.workloadLink`);

  // Workload table setup
  const { kind: addTableKind, id: addTableId } = useTableKindId("workload", "workload-firewall-internal");
  const qData = useTableItemQueryData<Workload>(addTableKind, {});

  const [selectedLinksToAdd, setSelectedLinksToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<WorkloadBasicTableItem[]>([]);
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  // Agent table setup
  const { kind: addAgentTableKind, id: addAgentTableId } = useTableKindId("agent", "workload-firewall-internal-agent");
  const agentQData = useTableItemQueryData<Agent>(addAgentTableKind, {});

  const [selectedAgentLinksToAdd, setSelectedAgentLinksToAdd] = React.useState<string[]>([]);
  const [agentSelectionItems, setAgentSelectionItems] = React.useState<AgentTableItem[]>([]);
  const [isAddingAgent, setIsAddingAgent] = React.useState<boolean>(false);

  // Workload form values
  const workloads = useCpFormValue(`firewallConfig.internal.workloads`);
  const workloadTableItems: WorkloadBasicTableItem[] = React.useMemo(() => {
    return workloads.map((w) => getWorkloadBasicTableItem({ item: w.item, link: w.link }));
  }, [workloads]);

  // Agent form values
  const agents = useCpFormValue(`firewallConfig.internal.agents`);
  const agentTableItems: AgentTableItem[] = React.useMemo(() => {
    return agents.map((a) => getAgentTableItem({ item: a.item, link: a.link }));
  }, [agents]);

  // Workload handlers
  const onRemoveItem = (index: number) => {
    const form = store.getState().form.instances[id];
    const values = form.values;
    const currentWorkloads = get(values, `firewallConfig.internal.workloads`);
    const _currentWorkloads = cloneDeep(currentWorkloads);
    _currentWorkloads.splice(index, 1);
    formSetValue(id, `firewallConfig.internal.workloads`, _currentWorkloads);
    formTriggerValidation(id);
  };

  function onOkAddModal() {
    try {
      const form = store.getState().form.instances[id];
      const values = form.values;
      const currentWorkloads = get(values, `firewallConfig.internal.workloads`);
      const _currentWorkloads = cloneDeep(currentWorkloads);
      for (const selectedLinkToAdd of selectedLinksToAdd) {
        const item = selectionItems.find((i) => i.selfLink === selectedLinkToAdd);
        if (_currentWorkloads.some((w) => w.link === selectedLinkToAdd)) {
          continue;
        }
        const parsedLink = parseLink(selectedLinkToAdd, { useInputCtx: true });
        const value: KindSelectState = {
          value: parsedLink.gvcRelative,
          link: selectedLinkToAdd,
        };
        if (item) {
          value.item = item;
        }
        _currentWorkloads.push(value);
      }
      formSetValue(id, `firewallConfig.internal.workloads`, _currentWorkloads);
      formTriggerValidation(id);
      setSelectedLinksToAdd([]);
      setSelectionItems([]);
      setIsAddingItem(false);
    } catch (e) {
      console.log("fail on onokaddmodal", e.message);
    }
  }

  // Agent handlers
  const onRemoveAgent = (index: number) => {
    const form = store.getState().form.instances[id];
    const values = form.values;
    const currentAgents = get(values, `firewallConfig.internal.agents`);
    const _currentAgents = cloneDeep(currentAgents);
    _currentAgents.splice(index, 1);
    formSetValue(id, `firewallConfig.internal.agents`, _currentAgents);
    formTriggerValidation(id);
  };

  function onOkAddAgentModal() {
    try {
      const form = store.getState().form.instances[id];
      const values = form.values;
      const currentAgents = get(values, `firewallConfig.internal.agents`);
      const _currentAgents = cloneDeep(currentAgents);
      for (const selectedLink of selectedAgentLinksToAdd) {
        const item = agentSelectionItems.find((i) => i.selfLink === selectedLink);
        if (_currentAgents.some((a) => a.link === selectedLink)) {
          continue;
        }
        const { name } = parseLink(selectedLink, { useInputCtx: true });
        const value: KindSelectState = {
          value: name,
          link: selectedLink,
        };
        if (item) {
          value.item = item;
        }
        _currentAgents.push(value);
      }
      formSetValue(id, `firewallConfig.internal.agents`, _currentAgents);
      formTriggerValidation(id);
      setSelectedAgentLinksToAdd([]);
      setAgentSelectionItems([]);
      setIsAddingAgent(false);
    } catch (e) {
      console.log("fail on onokaddagentmodal", e.message);
    }
  }

  // Filter out already-selected items from the add modals
  const currentLinks = workloads.map((w) => w.link);
  const addWorkloadTableItems: WorkloadBasicTableItem[] = qData.visibleItems
    .map((item) => getWorkloadBasicTableItem({ item }))
    .filter((item) => !currentLinks.includes(item.selfLink))
    .filter((item) => item.selfLink !== workloadLink);

  const currentAgentLinks = agents.map((a) => a.link);
  const addAgentTableItems: AgentTableItem[] = agentQData.visibleItems
    .map((item) => getAgentTableItem({ item }))
    .filter((item) => !currentAgentLinks.includes(item.selfLink));

  return (
    <>
      <CpFormItem>
        <CpLabel name={`skip`}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Inbound Allow List</span>
        </CpLabel>
        <div className="flex items-center gap-6 mb-4">
          <div className="flex items-center gap-2">
            <CpLabel name={`firewallConfig.internal.allowAccessToItself`}>Allow Access To Itself</CpLabel>
            <CpSwitch name={`firewallConfig.internal.allowAccessToItself`} />
          </div>
          <div className="flex items-center gap-2">
            <CpLabel name={`firewallConfig.internal.allowKedaAccess`}>Allow KEDA Access</CpLabel>
            <CpSwitch name={`firewallConfig.internal.allowKedaAccess`} />
          </div>
          <div className="flex items-center gap-2">
            <CpLabel name={`firewallConfig.internal.allowStorageAccess`}>Allow Storage Access</CpLabel>
            <CpSwitch name={`firewallConfig.internal.allowStorageAccess`} />
          </div>
        </div>
        <div style={{ fontWeight: 600 }}>Workloads</div>
        {workloadTableItems.length > 0 ? (
          <Table<WorkloadBasicTableItem>
            tableId="workload-firewall-internal-workload-items-editable"
            data={workloadTableItems}
            hideSettings
            styles={{ header: { marginBottom: 0 } }}
            columns={[
              NameDescriptionColumn({ enableSort: false }, { shouldForceTargetBlank: () => true }),
              GvcColumn(),
              TagsColumn(),
              {
                id: "action",
                label: "",
                enableResize: false,
                size: 120,
                cell: ({ row }) => {
                  return (
                    <div className="w-full flex justify-end">
                      <Button
                        htmlType={`button`}
                        type={"text"}
                        icon={<CloseOutlined />}
                        danger
                        onClick={() => onRemoveItem(row.index)}
                      />
                    </div>
                  );
                },
              },
            ]}
          />
        ) : null}
        <TableModal
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          onOk={() => {
            onOkAddModal();
          }}
        >
          <Table<WorkloadBasicTableItem>
            tableId={addTableId}
            title={"Select Workloads to Add"}
            selectMode="multi"
            selectKey="selfLink"
            selections={selectedLinksToAdd}
            onSelectionsChange={(keys, items) => {
              setSelectedLinksToAdd(keys);
              setSelectionItems(items!);
            }}
            data={addWorkloadTableItems}
            headerRenderer={() => {
              return (
                <>
                  <TableHeaderRefreshButton onClick={qData.fetchItems} />
                  <CustomTableCPLNQuery
                    tableId={addTableId}
                    kind={addTableKind}
                    onQuery={qData.setQuery}
                    isLoading={qData.isLoadingQueried}
                  />
                </>
              );
            }}
            enableFilter
            isLoading={qData.isLoading}
            onSortChange={qData.setSort}
            onSearchChange={qData.setSearch}
            isLoadingMore={qData.isLoadingMore}
            hasMore={qData.hasMore}
            onLoadMore={qData.fetchNextPage}
            columns={[
              NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
              GvcColumn(),
              TagsColumn(),
            ]}
          />
        </TableModal>
        <Button
          htmlType={`button`}
          className="mt-4"
          type={"dashed"}
          icon={<PlusCircleOutlined />}
          onClick={() => setIsAddingItem(true)}
        >
          Add Workload
        </Button>
        <div style={{ fontWeight: 600 }} className="mt-8">Agents</div>
        {agentTableItems.length > 0 ? (
          <Table<AgentTableItem>
              tableId="workload-firewall-internal-agent-items-editable"
              data={agentTableItems}
              hideSettings
              styles={{ header: { marginBottom: 0 } }}
              columns={[
                NameDescriptionColumn({ enableSort: false }, { shouldForceTargetBlank: () => true }),
                TagsColumn(),
                {
                  id: "action",
                  label: "",
                  enableResize: false,
                  size: 120,
                  cell: ({ row }) => {
                    return (
                      <div className="w-full flex justify-end">
                        <Button
                          htmlType={`button`}
                          type={"text"}
                          icon={<CloseOutlined />}
                          danger
                          onClick={() => onRemoveAgent(row.index)}
                        />
                      </div>
                    );
                  },
                },
              ]}
            />
        ) : null}
        <TableModal
          open={isAddingAgent}
          onCancel={() => setIsAddingAgent(false)}
          onOk={() => {
            onOkAddAgentModal();
          }}
        >
          <Table<AgentTableItem>
            tableId={addAgentTableId}
            title={"Select Agents to Add"}
            selectMode="multi"
            selectKey="selfLink"
            selections={selectedAgentLinksToAdd}
            onSelectionsChange={(keys, items) => {
              setSelectedAgentLinksToAdd(keys);
              setAgentSelectionItems(items!);
            }}
            data={addAgentTableItems}
            headerRenderer={() => {
              return (
                <>
                  <TableHeaderRefreshButton onClick={agentQData.fetchItems} />
                  <CustomTableCPLNQuery
                    tableId={addAgentTableId}
                    kind={addAgentTableKind}
                    onQuery={agentQData.setQuery}
                    isLoading={agentQData.isLoadingQueried}
                  />
                </>
              );
            }}
            enableFilter
            isLoading={agentQData.isLoading}
            onSortChange={agentQData.setSort}
            onSearchChange={agentQData.setSearch}
            isLoadingMore={agentQData.isLoadingMore}
            hasMore={agentQData.hasMore}
            onLoadMore={agentQData.fetchNextPage}
            columns={[
              NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
              TagsColumn(),
            ]}
          />
        </TableModal>
        <Button
          htmlType={`button`}
          className="mt-4"
          type={"dashed"}
          icon={<PlusCircleOutlined />}
          onClick={() => setIsAddingAgent(true)}
        >
          Add Agent
        </Button>
      </CpFormItem>
    </>
  );
};
